/**
 * The languages this build knows how to talk about.
 *
 * A static mirror of the platform's seeded `languages` rows (docs/i18n.md §2),
 * so the dashboard can name a language, pick its script direction and choose
 * an `Intl` locale before - or without - asking the API. The API stays the
 * authority on which languages are *active* and which a store has *enabled*;
 * this list only answers "what is `ta`?".
 *
 * Code refers to a language by its `code`, never by id.
 */

export type TextDirection = 'ltr' | 'rtl';

export interface LanguageMeta {
  /** Short code, as the API and URLs use it: `hi`. */
  code: string;
  /** The BCP 47 locale `Intl` formats with: `hi-IN`. */
  locale: string;
  /** English name: `Hindi`. */
  name: string;
  /** What the language calls itself: `हिन्दी`. */
  nativeName: string;
  direction: TextDirection;
  /** Whether the platform ships it switched on (the RTL seeds are not, yet). */
  active: boolean;
}

export const DEFAULT_LANGUAGE = 'en';

/** Language codes as the backend stores them: `hi`, `pt-br`, `zh-hant`. */
export const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}(?:-[a-z0-9]{2,8})?$/;

export const SUPPORTED_LANGUAGES: readonly LanguageMeta[] = [
  { code: 'en', locale: 'en-IN', name: 'English', nativeName: 'English', direction: 'ltr', active: true },
  { code: 'hi', locale: 'hi-IN', name: 'Hindi', nativeName: 'हिन्दी', direction: 'ltr', active: true },
  { code: 'bn', locale: 'bn-IN', name: 'Bengali', nativeName: 'বাংলা', direction: 'ltr', active: true },
  { code: 'mr', locale: 'mr-IN', name: 'Marathi', nativeName: 'मराठी', direction: 'ltr', active: true },
  { code: 'ta', locale: 'ta-IN', name: 'Tamil', nativeName: 'தமிழ்', direction: 'ltr', active: true },
  { code: 'te', locale: 'te-IN', name: 'Telugu', nativeName: 'తెలుగు', direction: 'ltr', active: true },
  { code: 'gu', locale: 'gu-IN', name: 'Gujarati', nativeName: 'ગુજરાતી', direction: 'ltr', active: true },
  { code: 'kn', locale: 'kn-IN', name: 'Kannada', nativeName: 'ಕನ್ನಡ', direction: 'ltr', active: true },
  { code: 'ml', locale: 'ml-IN', name: 'Malayalam', nativeName: 'മലയാളം', direction: 'ltr', active: true },
  { code: 'pa', locale: 'pa-IN', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', direction: 'ltr', active: true },
  { code: 'ar', locale: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl', active: false },
  { code: 'ur', locale: 'ur-IN', name: 'Urdu', nativeName: 'اردو', direction: 'rtl', active: false },
  { code: 'he', locale: 'he-IL', name: 'Hebrew', nativeName: 'עברית', direction: 'rtl', active: false },
];

const BY_CODE = new Map(SUPPORTED_LANGUAGES.map((language) => [language.code, language]));

/** Scripts written right to left, for a code this build has no row for. */
const RTL_PRIMARY = new Set(['ar', 'ur', 'he', 'fa', 'ps', 'sd', 'yi', 'dv', 'ug', 'ckb']);

/** `hi-IN`, `HI`, `hi_in` -> `hi-in`. */
function normalise(candidate: string): string {
  return candidate.trim().toLowerCase().replace(/_/g, '-');
}

/**
 * What is known about a language code.
 *
 * Never undefined: a code this build has no row for (a language the platform
 * added after this bundle shipped) gets a best-effort entry - its code as its
 * name and its direction guessed from the primary subtag - so a switcher can
 * still render it rather than crash.
 */
export function languageMeta(code: string): LanguageMeta {
  const key = normalise(code);
  const primary = key.split('-')[0];
  // `hi-IN` is Hindi spelled as a locale; the primary subtag names the row.
  const known = BY_CODE.get(key) ?? BY_CODE.get(primary);
  if (known) return known;
  return {
    code: key,
    locale: code,
    name: code,
    nativeName: code,
    direction: RTL_PRIMARY.has(primary) ? 'rtl' : 'ltr',
    active: false,
  };
}

export function isRtl(code: string): boolean {
  return languageMeta(code).direction === 'rtl';
}

/** The `Intl` locale for a code: `hi` -> `hi-IN`. */
export function localeFor(code: string): string {
  return languageMeta(code).locale;
}

/**
 * The enabled language a loose candidate names, if any.
 *
 * Accepts a code (`hi`), a locale (`hi-IN`), any casing (`HI`) or an
 * underscore (`hi_IN`), the way a URL, `navigator.language` or a saved cookie
 * might spell it. An exact code wins over a primary-subtag match, so a store
 * that enables both `pt` and `pt-br` answers `pt-BR` with `pt-br`.
 */
export function matchLanguage(
  candidate: string | null | undefined,
  enabledCodes: readonly string[],
): string | null {
  if (!candidate) return null;
  const wanted = normalise(candidate);
  if (!wanted) return null;
  const enabled = enabledCodes.map((code) => normalise(code));

  const exact = enabled.indexOf(wanted);
  if (exact !== -1) return enabledCodes[exact];

  // A locale for a known code: `hi-in` is Hindi's locale, not a code.
  const byLocale = enabled.findIndex((code) => normalise(languageMeta(code).locale) === wanted);
  if (byLocale !== -1) return enabledCodes[byLocale];

  const primary = wanted.split('-')[0];
  const byPrimary = enabled.indexOf(primary);
  return byPrimary !== -1 ? enabledCodes[byPrimary] : null;
}
